import React, { useState, useEffect, useMemo } from "react";
import api from "../services/api";

function timeAgo(isoString) {
  if (!isoString) return "Never";
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatDateTime(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

function activityStatus(isoString) {
  if (!isoString) return "offline";
  const mins = (Date.now() - new Date(isoString).getTime()) / 60000;
  if (mins < 30) return "online";
  if (mins < 60 * 24) return "recent";
  return "offline";
}

const PAGE_COLORS = {
  "Home": "#0f766e", "Inbox": "#0284c7", "Messages": "#7c3aed",
  "Discussions": "#7c3aed", "Reports": "#475569", "Surveys": "#0891b2",
  "Insights": "#0891b2", "New Project": "#16a34a", "Assign": "#dc2626",
  "Change Log": "#64748b", "Job Tracker": "#0891b2", "Timesheet": "#059669",
  "Norms": "#b45309", "BCM Norms": "#b45309", "Driver Analysis": "#dc2626",
  "Open End Coding": "#4f46e5", "Analysis Warning Note": "#6d28d9",
  "Image Resizing": "#0369a1", "Resources": "#047857", "Vendors": "#92400e",
  "PO Tracker": "#b45309", "PO Report": "#b45309",
};

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = [
  "#0d7377", "#4f46e5", "#059669", "#b45309", "#7c3aed",
  "#0891b2", "#dc2626", "#047857", "#0369a1", "#92400e",
];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

const STATUS_CFG = {
  online:  { dot: "#22c55e", label: "Online",       bg: "#f0fdf4", color: "#166534" },
  recent:  { dot: "#f59e0b", label: "Active today",  bg: "#fffbeb", color: "#92400e" },
  offline: { dot: "#94a3b8", label: "Offline",       bg: "#f8fafc", color: "#475569" },
};

function PageChip({ page, at }) {
  const color = PAGE_COLORS[page] || "#6b7280";
  return (
    <span
      title={formatDateTime(at)}
      style={{
        display: "inline-block",
        padding: "2px 9px",
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 600,
        background: `${color}15`,
        color: color,
        border: `1px solid ${color}35`,
        whiteSpace: "nowrap",
        cursor: "default",
      }}
    >
      {page}
    </span>
  );
}

export default function AdminActivity() {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery]   = useState("");
  const [error, setError]   = useState("");

  useEffect(() => {
    api.get("users/admin-activity/")
      .then(res => setRows(res.data))
      .catch(err => {
        if (err.response?.status === 403) setError("Admin access required.");
        else setError("Failed to load activity data.");
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return rows
      .filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q)
      )
      .sort((a, b) => {
        const aAt = a.recent_pages[0]?.at || null;
        const bAt = b.recent_pages[0]?.at || null;
        if (!aAt && !bAt) return 0;
        if (!aAt) return 1;
        if (!bAt) return -1;
        return new Date(bAt) - new Date(aAt);
      });
  }, [rows, query]);

  const onlineCount = rows.filter(r => activityStatus(r.recent_pages[0]?.at) === "online").length;

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
      <div style={{ textAlign: "center", color: "#64748b" }}>
        <div style={{ width: 36, height: 36, border: "3px solid #e2e8f0", borderTopColor: "#0d7377", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        Loading activity…
      </div>
    </div>
  );

  if (error) return (
    <div style={{ textAlign: "center", padding: 48, color: "#dc2626" }}>{error}</div>
  );

  return (
    <div style={{ padding: "28px 24px 48px", maxWidth: 1200, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", letterSpacing: "-.4px", margin: 0 }}>
            User Activity
          </h1>
          <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              {rows.length} users
            </span>
            {onlineCount > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "#166534", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 99, padding: "2px 10px" }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", display: "inline-block" }} />
                {onlineCount} online now
              </span>
            )}
          </div>
        </div>

        {/* Search */}
        <div style={{ position: "relative", width: 280 }}>
          <svg style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search by name or email…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{
              width: "100%", paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8,
              fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 8,
              outline: "none", background: "#fff", color: "#0f172a",
              boxShadow: "0 1px 2px rgba(0,0,0,.04)",
            }}
          />
        </div>
      </div>

      {/* Table card */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              {["User", "Last Login", "Recent Pages", "Status"].map(h => (
                <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "#64748b" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, idx) => {
              const lastActive = row.recent_pages[0]?.at || null;
              const status = activityStatus(lastActive);
              const cfg = STATUS_CFG[status];
              const initials = getInitials(row.name);
              const bgColor = avatarColor(row.name);

              // dedupe pages preserving first occurrence order
              const seenPages = new Set();
              const uniquePages = row.recent_pages.filter(p => {
                if (seenPages.has(p.page)) return false;
                seenPages.add(p.page);
                return true;
              });

              return (
                <tr
                  key={row.id}
                  style={{
                    borderBottom: idx < filtered.length - 1 ? "1px solid #f1f5f9" : "none",
                    transition: "background .1s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  {/* User cell */}
                  <td style={{ padding: "14px 16px", verticalAlign: "middle", minWidth: 200 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: "50%",
                          background: bgColor, color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 13, fontWeight: 700, letterSpacing: ".5px",
                        }}>
                          {initials}
                        </div>
                        <span style={{
                          position: "absolute", bottom: 1, right: 1,
                          width: 9, height: 9, borderRadius: "50%",
                          background: cfg.dot, border: "1.5px solid #fff",
                        }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{row.name}</div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>{row.email}</div>
                      </div>
                    </div>
                  </td>

                  {/* Last Login */}
                  <td style={{ padding: "14px 16px", verticalAlign: "middle", minWidth: 150 }}>
                    <div style={{ fontSize: 13, color: "#374151" }}>{formatDateTime(row.last_login)}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{timeAgo(row.last_login)}</div>
                  </td>

                  {/* Recent Pages */}
                  <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                    {uniquePages.length === 0 ? (
                      <span style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>No activity yet</span>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {uniquePages.map((p, i) => (
                          <PageChip key={i} page={p.page} at={p.at} />
                        ))}
                      </div>
                    )}
                  </td>

                  {/* Status */}
                  <td style={{ padding: "14px 16px", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                    <div>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 99, background: cfg.bg, color: cfg.color }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, display: "inline-block" }} />
                        {cfg.label}
                      </span>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4, paddingLeft: 2 }}>
                        {timeAgo(lastActive)}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}

            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: "center", padding: "36px 16px", color: "#94a3b8", fontSize: 14 }}>
                  No users match "{query}"
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
